import { FEATURES_CONFIG } from "../../../featuresConfig";
import StorageManager from "../../../utils/StorageManager";
import { moneyDiag } from "../../../utils/moneyTrace";
import { requestJson } from "../../requestJson";
import { PROVIDER_CONFIG } from "./providerConfig";

function mapSymbol(symbolId) {
  const n = typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;
  const map = PROVIDER_CONFIG.SYMBOL_MAP;
  return map[n] !== undefined ? map[n] : n;
}

function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) return screenData;
  return screenData.map((reel) => (Array.isArray(reel) ? reel.map(mapSymbol) : reel));
}

function normalizeErrorCodes(payload) {
  if (Array.isArray(payload?.errors)) {
    payload.errors.forEach((error) => {
      if (error && PROVIDER_CONFIG.ERROR_CODES[error.code] !== undefined) {
        error.code = PROVIDER_CONFIG.ERROR_CODES[error.code];
      }
    });
  }
  return payload;
}

function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

const spinWorkerCode = `
  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", options } = event.data;

    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "Dmon: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      const requestBody = { command: command, options: { bet: bet, ...(options || {}) } };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          [csrfTokenName]: csrfTokenValue,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      // Read the body before judging the status: an error response carries the
      // codes the popup needs, and throwing on the status would discard them.
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (data && Array.isArray(data.errors) && data.errors.length > 0) {
        postMessage({ error: data });
        return;
      }

      if (!response.ok) {
        postMessage({ error: "Dmon: Spin API call failed: HTTP " + response.status });
        return;
      }

      if (!data) {
        postMessage({ error: "Dmon: Spin API call failed: Invalid response structure" });
        return;
      }

      postMessage(data);

    } catch (error) {
      postMessage({ error: "Dmon: Spin API call failed: " + ((error && error.message) || error) });
    }
  };
`;

const traceServerMoney = (command, data) => {
  const fr = data?.free_rounds;
  moneyDiag(
    `server:${command}`,
    `wallet=${data?.balance?.wallet ?? "-"} game=${data?.balance?.game ?? "-"}` +
      ` outcome.win=${data?.outcome?.win ?? "-"} state=${data?.flow?.state ?? "-"}` +
      (fr
        ? ` | fr=${fr.status ?? "-"} ${fr.performed ?? "-"}/${fr.count ?? "-"}` +
          ` fr.balance=${data?.balance?.free_rounds ?? "-"}`
        : ""),
  );
};

const freeRoundsCanceledError = () => ({
  errors: [{ code: 206, desc: "free_rounds_canceled" }],
});

class DmonRGS {
  constructor() {
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null;
    this.currentToken = null;
  }

  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);
    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }
    return "regular_win";
  }

  detectExpandingWilds(reels, wins) {
    if (!reels || !wins || !Array.isArray(wins) || wins.length === 0) return [];
    const wildId = PROVIDER_CONFIG.WILD_SYMBOL_ID;
    if (wildId === null || wildId === undefined) return [];

    const middleReelIndices = [1, 2, 3];
    const reelsWithWilds = [];

    for (const reelIndex of middleReelIndices) {
      const visibleSymbols = reels[reelIndex];
      if (!visibleSymbols || !Array.isArray(visibleSymbols)) continue;

      const wildPosition = visibleSymbols.findIndex((symbolId) => symbolId === wildId);
      if (wildPosition === -1) continue;

      const isPartOfConnectedWin = wins.some((win) => {
        // A scatter pays anywhere, so it never ties a wild to a line.
        if (win.isScatter) return false;
        if (!Array.isArray(win.positions) || win.positions.length === 0) return false;
        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);
        if (!uniqueReels.includes(reelIndex)) return false;

        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];
          if (i === 0 && currentReel > 1) break;
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) break;
          rightmostConnected = currentReel;
        }
        return rightmostConnected >= reelIndex;
      });

      if (isPartOfConnectedWin) {
        reelsWithWilds.push({ reelIndex, wildPosition });
      }
    }
    return reelsWithWilds;
  }

  /**
   * Normalize raw wins from the API (tuple format) into object format.
   * `screen` must be the RAW server screen — symbols are mapped here, once.
   *
   *   Line win:    ["line", amount, [row0, row1, ...], lineId]
   *   Scatter win: ["scatter", amount, [[reel,row], ...]] or with a 4th count element
   */
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    const wildId = PROVIDER_CONFIG.WILD_SYMBOL_ID;
    const symbolAt = (reel, row) => {
      const raw = screen?.[reel]?.[row];
      return raw === undefined ? null : mapSymbol(raw);
    };

    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple)) return null;

        if (winTuple[0] === "scatter") {
          const [, amount, positions] = winTuple;
          if (!Array.isArray(positions)) return null;
          const cells = positions.filter((p) => Array.isArray(p) && p.length >= 2);
          return {
            id: null,
            win: amount || 0,
            positions: cells.map(([reel, row]) => [reel, row]),
            symbol: cells.length > 0 ? symbolAt(cells[0][0], cells[0][1]) : null,
            isScatter: true,
          };
        }

        if (winTuple.length < 4) return null;
        const [, amount, positions, lineId] = winTuple;
        if (!Array.isArray(positions)) return null;

        const transformedPositions = positions.map((rowValue, reelIndex) =>
          Array.isArray(rowValue) && rowValue.length >= 2
            ? [rowValue[0], rowValue[1]]
            : [reelIndex, rowValue],
        );

        // Winning symbol — first non-wild on the line; all wilds → the first symbol
        let symbol = null;
        for (const [reel, row] of transformedPositions) {
          const mapped = symbolAt(reel, row);
          if (mapped !== null && mapped !== wildId) {
            symbol = mapped;
            break;
          }
        }
        if (symbol === null && transformedPositions.length > 0) {
          const [reel, row] = transformedPositions[0];
          symbol = symbolAt(reel, row);
        }

        return {
          id: lineId,
          win: amount,
          positions: transformedPositions,
          symbol,
        };
      })
      .filter((w) => w !== null);
  }

  adaptSpinRoundToState(normalizedOutcome, bet) {
    if (!normalizedOutcome) return null;

    const totalWin = normalizedOutcome.win || 0;
    const winType = this.computeWinType(totalWin, bet);
    const reels = normalizedOutcome.reels || [];
    const wins = normalizedOutcome.wins || [];

    const round = {
      reels,
      wins,
      win: totalWin,
      bet,
      winType,
      isAnimatedWin:
        winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
      storage: normalizedOutcome.storage || null,
    };

    if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED) {
      round.anticipation = FEATURES_CONFIG.TENSION.ANTICIPATION.FORCE
        ? true
        : (normalizedOutcome.anticipation || false);
    }

    if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED) {
      round.expandingWildReels = this.detectExpandingWilds(reels, wins);
      round.hasExpandingWilds = round.expandingWildReels.length > 0;
    }

    return round;
  }

  /**
   * A stored round has no id (saveBaseGameRound whitelists its fields), and may
   * predate the replay flags. The win controller returns at `!triggerWin` and
   * dedupes on `id`, so both are derived here.
   */
  adaptReloadedRound(round, fallbackBet) {
    if (!round) return round;
    const roundWin = round.win || 0;
    const roundBet = round.bet || fallbackBet || 0;
    const winType = round.winType || this.computeWinType(roundWin, roundBet);
    return {
      ...round,
      bet: roundBet,
      winType,
      isAnimatedWin:
        round.isAnimatedWin ??
        (winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType)),
      triggerWin: round.triggerWin || roundWin > 0,
      // Stable across repeated inits (heartbeat): a new id would replay the win.
      id: round.id ?? `dmon-reload-${roundWin}-${(round.reels || []).flat().join(".")}`,
    };
  }

  async initGame(useApiData = false, ignoreFreeRoundsStorage = false) {
    if (!window.__OPTIONS__) {
      throw new Error("Dmon: window.__OPTIONS__ not found.");
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`
      );
    }

    try {
      const response = await requestJson(apiUrl, {
        method: "POST",
        headers: {
          [csrfHeaderName]: csrfHeaderValue,
          "Content-Type": "application/json",
        },
        body: { command: "init" },
      });

      if (!response.data) {
        throw new Error("Dmon: Invalid API response - response.data is missing");
      }
      traceServerMoney("init", response.data);

      if (Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        throw normalizeErrorCodes({ errors: response.data.errors });
      }

      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;

      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error("Dmon: Invalid API response - options.available_bets is missing or invalid");
      }
      if (typeof defaultBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.default_bet is missing or invalid");
      }
      if (typeof baseBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.base_bet is missing or invalid");
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error("Dmon: Invalid API response - options.paytable is missing or invalid");
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error("Dmon: Invalid API response - options.lines is missing or invalid");
      }
      if (typeof wallet !== "number") {
        throw new Error("Dmon: Invalid API response - balance.wallet is missing or invalid");
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error("Dmon: Invalid API response - options.currency.code is missing or invalid");
      }

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };

      const freeRoundsData = response.data?.free_rounds || null;
      const freeRoundsTotalWin = response.data?.balance?.free_rounds ?? 0;

      // FR canceled externally (operator revoked) — 206 drives the error recovery overlay
      if (freeRoundsData?.status === "canceled") {
        StorageManager.clearFreeRoundsRound();
        throw freeRoundsCanceledError();
      }

      const storedState = this.loadGameState(response.data);
      const balance = wallet;
      // Loaded BEFORE base_game_round can be overwritten below
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      const round =
        useApiData || !storedState.round
          ? this.extractRoundFromInit(response.data)
          : this.adaptReloadedRound(storedState.round, bet);

      const result = {
        balance,
        bet,
        baseBet,
        bets: availableBets,
        round,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        game: identifier,
        gameMode: "base",
        featureState: null,
        reloadState: storedState.reloadState,
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        holdAndWin: null,
        gamble: null,
        freeSpinsState: null,
        freeRounds: freeRoundsData,
        freeRoundsTotalWin,
        command: "init",
        jackpotMultipliers: null,
        coinValues: null,
        progress: null,
      };

      if (result.round && (!StorageManager.hasBaseGameRound() || useApiData)) {
        StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
      }

      if (freeRoundsData?.status === "active") {
        if (StorageManager.hasFreeRoundsRound()) {
          // Exists → update changing fields, never touch betToRestore
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
          });
        } else {
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
            betToRestore: savedBaseGame?.bet ?? result.bet,
          });
        }

        // FR round 0: no round played yet — fresh reels with no wins, so a
        // reload does not replay the previous base game win.
        if (freeRoundsData.performed === 0) {
          const freshRound = this.extractRoundFromInit(response.data);
          if (freshRound) {
            StorageManager.saveBaseGameRound({
              round: freshRound,
              bet: savedBaseGame?.bet ?? result.bet,
              balance: result.balance,
            });
            result.round = freshRound;
          }
        }
      }

      // FR Completion Reload: the API is back to base, but the stored record
      // still owes the completion popups until the next base spin. A record
      // with performed < count was canceled externally, not completed.
      if (!freeRoundsData?.status && StorageManager.hasFreeRoundsRound()) {
        const savedFR = ignoreFreeRoundsStorage ? null : StorageManager.loadFreeRoundsRound();
        if (savedFR && savedFR.performed >= savedFR.count) {
          result.freeRounds = {
            status: "played",
            bet_value: savedFR.betValue,
            performed: savedFR.performed,
            count: savedFR.count,
            lastRoundApplied: savedFR.lastRoundApplied ?? true,
          };
          result.freeRoundsTotalWin = savedFR.win ?? 0;
        } else {
          StorageManager.clearFreeRoundsRound();
        }
      }

      return result;
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        throw normalizeErrorCodes({ errors: error.response.data.errors });
      }
      if (error.message?.startsWith("Dmon:")) throw error;
      throw new Error(`Dmon: Init API call failed: ${error.message}`);
    }
  }

  spin(bet, token, command, purchasedFeature, spinOptions) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(new Error("Dmon: window.__OPTIONS__ not found."));
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(new Error(`Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`));
        return;
      }

      // Every path settles: an exception while adapting the response must not
      // leave the spin promise pending.
      this.worker.onmessage = (event) => {
        try {
          resolve(this.handleSpinResponse(event.data, bet, command));
        } catch (error) {
          if (error?.errors && Array.isArray(error.errors)) {
            reject(error);
          } else {
            reject(new Error(`Dmon: Spin response handling failed: ${error?.message || error}`));
          }
        }
      };

      this.worker.onerror = (event) => {
        // A worker that has errored is dead — drop it so the next spin builds a fresh one.
        const dead = this.worker;
        this.worker = null;
        try {
          dead?.terminate();
        } catch {
          // Already gone.
        }
        reject(new Error(`Dmon: Worker error: ${event?.message || "Unknown worker error"}`));
      };

      const tokenToUse = token || this.currentToken || playToken;

      this.worker.postMessage({
        bet,
        token: tokenToUse,
        apiUrl,
        csrfTokenName: csrfTokenHeaderName,
        csrfTokenValue: csrfTokenHeaderValue,
        identifier,
        command,
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  /**
   * Turn one worker message into the spin result. Throws the structured
   * `{errors}` shape for server errors and a canceled free-rounds campaign.
   */
  handleSpinResponse(data, bet, command) {
    if (!data || typeof data !== "object") {
      throw new Error("Empty worker message");
    }

    if (data.error) {
      let extractedBalance = null;
      if (typeof data.error === "object" && data.error.balance) {
        if (typeof data.error.balance === "object" && data.error.balance.wallet !== undefined) {
          extractedBalance = data.error.balance.wallet;
        } else if (typeof data.error.balance === "number") {
          extractedBalance = data.error.balance;
        }
      }

      if (typeof data.error === "object" && Array.isArray(data.error.errors)) {
        normalizeErrorCodes(data.error);
        if (extractedBalance !== null) data.error.balance = extractedBalance;
        throw data.error;
      }

      const normalizedError = {
        errors: [{ code: 999, desc: typeof data.error === "string" ? data.error : "Unknown error" }],
      };
      if (extractedBalance !== null) normalizedError.balance = extractedBalance;
      throw normalizedError;
    }

    traceServerMoney(command, data);

    if (data.flow?.round_id) {
      this.currentToken = data.flow.round_id;
    }

    const freeRoundsData = data.free_rounds || null;
    const freeRoundsTotalWin = data.balance?.free_rounds ?? null;

    // FR revoked mid-session — 206 drives the error recovery overlay
    if (freeRoundsData?.status === "canceled") {
      StorageManager.clearFreeRoundsRound();
      throw freeRoundsCanceledError();
    }

    let extractedBalance = data.balance;
    if (data.balance && typeof data.balance === "object" && data.balance.wallet !== undefined) {
      extractedBalance = data.balance.wallet;
    }

    // The screen is mapped ONCE; wins read the raw copy and map their own symbols.
    let normalizedOutcome = null;
    if (data.outcome) {
      const rawScreen = Array.isArray(data.outcome.screen) ? data.outcome.screen : null;
      const reels = normalizeScreenData(rawScreen);
      normalizedOutcome = {
        ...data.outcome,
        reels,
        screen: reels,
        wins: this.normalizeWins(data.outcome.wins, rawScreen),
        win: data.balance?.game ?? data.outcome.win,
      };

      const anticipation = data.features?.anticipation ?? data.anticipation;
      if (anticipation !== undefined) {
        normalizedOutcome.anticipation = anticipation;
      }
    }

    const winAmount = normalizedOutcome?.win || 0;
    const finalBalance = extractedBalance + winAmount;

    const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, bet);
    if (adaptedRound) {
      adaptedRound.id = data.flow?.round_id ?? `dmon-${Date.now()}`;
      adaptedRound.triggerWin = adaptedRound.win > 0;
    }
    const round = adaptedRound || normalizedOutcome;
    const roundBet = data.outcome?.bet || bet || null;

    // Base spin outside a campaign: the finished campaign's record is spent.
    if (command === "spin") {
      const currentFR = StorageManager.loadFreeRoundsRound();
      if (!currentFR || currentFR.status !== "active") {
        this.clearCompletionData();
      }
    }

    // Read BEFORE the save below overwrites it with this spin's bet
    const previousBaseBet = StorageManager.loadBaseGameRound()?.bet ?? null;

    if (round) {
      StorageManager.saveBaseGameRound({ round, bet: roundBet, balance: finalBalance });
    }

    if (freeRoundsData) {
      if (StorageManager.hasFreeRoundsRound()) {
        StorageManager.saveFreeRoundsRound({
          status: freeRoundsData.status,
          betValue: freeRoundsData.bet_value,
          performed: freeRoundsData.performed,
          count: freeRoundsData.count,
          win: freeRoundsTotalWin ?? StorageManager.loadFreeRoundsRound()?.win ?? 0,
          // This round's own win is folded into `win` only once its reveal
          // plays (WinDisplay → updateFreeRoundsWin).
          lastRoundApplied: false,
        });
      } else {
        StorageManager.saveFreeRoundsRound({
          status: freeRoundsData.status,
          betValue: freeRoundsData.bet_value,
          performed: freeRoundsData.performed,
          count: freeRoundsData.count,
          win: freeRoundsTotalWin ?? 0,
          betToRestore: previousBaseBet,
          lastRoundApplied: false,
        });
        // A reload at FR round 0 must not replay a base game win
        StorageManager.stripBaseGameWins();
      }
    }

    return {
      token: data.flow?.round_id || null,
      bet: roundBet,
      balance: finalBalance,
      round,
      gameMode: "base",
      featureState: null,
      freeSpinsState: null,
      availableActions: data.flow?.available_actions || ["init", "spin"],
      flowState: data.flow?.state || "ready",
      features: data.features || null,
      holdAndWin: null,
      gamble: null,
      freeSpinsTrigger: null,
      freeRounds: freeRoundsData,
      freeRoundsTotalWin,
      command,
      progress: null,
      respinRound: null,
      jackpotMultipliers: null,
      coinValues: null,
    };
  }

  getSpinParams({ bet, freeRoundsActive, freeRoundsBet }) {
    if (freeRoundsActive) {
      return { command: "spin", spinOptions: { bonus: true, bet: freeRoundsBet || bet } };
    }
    return { command: "spin", spinOptions: undefined };
  }

  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  getBalanceStrategy() {
    return {
      serverIncludesWin: false,
      clientDeductsBet: true,
      clientAddsWin: true,
    };
  }

  shouldSyncBalanceOnWinClose() {
    return true;
  }

  getCurrentToken() {
    return this.currentToken || null;
  }

  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    if (savedBaseGame) {
      return { round: savedBaseGame.round, bet: savedBaseGame.bet, reloadState: { needsReload: false } };
    }
    return { reloadState: { needsReload: false } };
  }

  loadGameState() {
    return this.buildBaseGameReload();
  }

  clearCompletionData() {
    StorageManager.clearFreeRoundsRound();
  }

  extractRoundFromInit(apiResponse) {
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      return { reels: normalizeScreenData(screen), wins: [], win: 0 };
    }
    return null;
  }
}

export { spinWorkerCode };

export default DmonRGS;
